import { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DoorOpen, Loader2, Trophy } from "lucide-react";
import ProductionGrid from "./ProductionGrid";

const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

export default function QuickPanel({ ctx, officeId, cross, onChanged }) {
  const [busy, setBusy] = useState(null);
  const busyRef = useRef(false);
  const [lastVisit, setLastVisit] = useState(null);
  const [note, setNote] = useState("");
  const [qty, setQty] = useState("1");
  const needOffice = cross && !officeId;

  const post = async (key, path, body) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(key);
    try {
      const { data } = await api.post(path, { client_request_id: newId(), office_id: officeId || undefined, ...body });
      return data;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore registrazione"); }
    finally { busyRef.current = false; setBusy(null); }
  };
  const visit = async () => {
    const n = parseInt(qty, 10);
    if (!Number.isInteger(n) || n < 1) { toast.error("Numero ingressi: inserisci un intero positivo"); return; }
    const d = await post("visit", "/monitoring/visits", { notes: note || undefined, quantity: n });
    if (d) { setLastVisit(d); setNote(""); setQty("1"); toast.success(d.quantity === 1 ? "Ingresso registrato" : `${d.quantity} ingressi registrati`); onChanged(); }
  };

  if (!ctx) return <div className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-brand" /></div>;
  const fmtD = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  return (
    <div className="space-y-4" data-testid="quick-panel">
      <div className="rounded-lg border border-border p-3" data-testid="info-block">
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-700">
          <span>Sede: <b data-testid="qp-office">{ctx.office_name}</b></span>
          <span>Operatore: <b data-testid="qp-operator">{ctx.operator_name}</b></span>
          <span>Data: <b data-testid="qp-date">{fmtD(ctx.date)}</b></span>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2 lg:grid-cols-4">
          <Stat label="Pedonabilità oggi" hint="ingressi sede" value={ctx.visits_today} testid="qp-visits" tone="text-brand" />
          <Stat label="Operazioni oggi" hint="contratti sede" value={ctx.operations_today} testid="qp-operations" tone="text-emerald-600" />
          <Stat label="Pedonabilità mese" hint={`dal ${fmtD(ctx.month_start)}`} value={ctx.visits_month} testid="qp-visits-month" tone="text-brand" />
          <Stat label="Operazioni mese" hint={`dal ${fmtD(ctx.month_start)}`} value={ctx.operations_month} testid="qp-operations-month" tone="text-emerald-600" />
        </div>
      </div>
      {needOffice && <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700" data-testid="qp-need-office">Seleziona una sede in alto per registrare.</p>}
      <div className="rounded-lg border border-border p-3" data-testid="visits-block">
        <h3 className="mb-2 text-sm font-semibold text-slate-700">Pedonabilità — ingressi clienti</h3>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button onClick={visit} disabled={!!busy || needOffice} className="h-16 flex-1 gap-2 text-lg font-semibold" data-testid="qp-visit-button">
            {busy === "visit" ? <Loader2 className="h-5 w-5 animate-spin" /> : <DoorOpen className="h-6 w-6" />} + REGISTRA INGRESSO
          </Button>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <span className="whitespace-nowrap">Numero ingressi</span>
            <input type="number" inputMode="numeric" min={1} step={1} value={qty} onChange={(e) => setQty(e.target.value.replace(/[^0-9]/g, ""))} onFocus={(e) => e.target.select()}
              className="h-12 w-20 rounded-md border-2 border-border text-center text-xl font-bold text-slate-800 focus:border-brand focus:outline-none" data-testid="qp-visit-qty" />
          </label>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota ingresso (facoltativa)" className="h-10 rounded-md border border-border px-2 text-sm sm:w-64" data-testid="qp-visit-note" />
        </div>
        <p className="mt-1 text-[11px] text-slate-500">I tuoi ingressi di oggi: <b data-testid="qp-my-visits">{ctx.my_visits_today}</b>. Gli ingressi restano separati dalle operazioni commerciali.</p>
        {lastVisit && (
          <div className="mt-2 rounded-md bg-brand/5 px-3 py-1.5 text-xs text-slate-600" data-testid="qp-last-visit">
            {lastVisit.quantity > 1 ? `${lastVisit.quantity} ingressi registrati` : "Ultimo ingresso registrato"} alle {new Date(lastVisit.occurred_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}.
          </div>
        )}
      </div>
      <ProductionGrid ctx={ctx} officeId={officeId} disabled={needOffice} onSaved={onChanged} />
      <div>
        <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700"><Trophy className="h-4 w-4 text-amber-500" /> Obiettivi in corso</h3>
        {ctx.goals.length === 0 ? <p className="text-xs text-slate-400" data-testid="qp-goals-empty">Nessun obiettivo attivo per il periodo corrente.</p> : (
          <div className="grid gap-2 sm:grid-cols-2" data-testid="qp-goals">{ctx.goals.map((g) => <GoalCard key={g.id} g={g} />)}</div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, testid, tone = "text-slate-800", hint }) {
  return (
    <div className="rounded-md border border-border px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-3xl font-bold ${tone}`} data-testid={testid}>{value ?? "—"}</div>
      {hint && <div className="text-[10px] text-slate-400">{hint}</div>}
    </div>
  );
}

export const STATUS_TONE = {
  "NON INIZIATO": "bg-slate-100 text-slate-500", "IN CORSO": "bg-sky-100 text-sky-700", "QUASI RAGGIUNTO": "bg-amber-100 text-amber-700",
  "RAGGIUNTO": "bg-emerald-100 text-emerald-700", "SUPERATO": "bg-emerald-600 text-white",
};

export function GoalCard({ g, children }) {
  const pct = Math.min(g.percent, 100);
  return (
    <div className={`rounded-lg border p-3 ${g.reached ? "border-emerald-400 bg-emerald-50/60" : "border-border"}`} data-testid={`goal-${g.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-1">
        <div className="text-sm font-semibold text-slate-800">{g.operation_type_name} — {g.office_name}</div>
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${STATUS_TONE[g.status]}`} data-testid={`goal-status-${g.id}`}>{g.status}</span>
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-2xl font-bold text-slate-800" data-testid={`goal-progress-${g.id}`}>{g.current} / {g.target_count}</span>
        <span className="text-xs text-slate-500">{g.percent}% · {g.period_type} · {new Date(g.period_start).toLocaleDateString("it-IT")} → {new Date(g.period_end).toLocaleDateString("it-IT")}</span>
      </div>
      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-slate-100"><div className={`h-full ${g.reached ? "bg-emerald-500" : "bg-brand"}`} style={{ width: `${pct}%` }} /></div>
      <div className="mt-1 flex flex-wrap justify-between gap-1 text-[11px] text-slate-500">
        <span>{g.reached ? <span className="font-semibold text-emerald-700" data-testid={`goal-reached-${g.id}`}>OBIETTIVO RAGGIUNTO</span> : `Mancano ${g.missing}`}{g.days_left ? ` · ${g.days_left} giorni rimanenti` : ""}</span>
        <span className="font-medium">🏅 {g.badge_name}</span>
      </div>
      {children}
    </div>
  );
}
